/* eslint-disable react-hooks/exhaustive-deps */
import { Mutation } from "@apollo/client/react/components";
import { Provider } from "jotai";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { FormattedMessage } from "react-intl";

import EmployeesChart from "components/charts/modern/EmployeesChart";
import FreeFloatChart from "components/charts/modern/FreeFloatChart";
import IndicatorVsQuoteChart from "components/charts/modern/IndicatorVsQuoteChart";
import MarketCapitalizationChart from "components/charts/modern/MarketCapitalizationChart";
import PayRatioHistoryChart from "components/charts/modern/PayRatioHistoryChart";
import QuotesChart from "components/charts/modern/QuotesChart";
import SayOnPayChart from "components/charts/modern/SayOnPayChart";
import SharesChart from "components/charts/modern/SharesChart";
import TradesChart from "components/charts/modern/TradesChart";
import TrailingReturnsChart from "components/charts/modern/TrailingReturnsChart";
import { SectionJumpIdentifiers } from "components/company/SectionJumpIdentifiers";
import StaticContentSection from "components/company/StaticContentSection";
import ContentSectionHeading from "components/company/sections/ContentSectionHeading";
import ExecutivesAndDirectors from "components/company/sections/collapsible/ExecutivesAndDirectors";
import CompanyIssuerGovernance from "components/company/v2/CompanyIssuerGovernance";
import CompanyIssuerHeaderCard from "components/company/v2/CompanyIssuerHeaderCard";
import CompanyIssuerRatings from "components/company/v2/CompanyIssuerRatings";
import YouTubeVideos from "components/company/v2/YouTubeVideos";
import EmployeesCountCard from "components/company/v2/cards/EmployeesCountCard";
import ExchangeRegistrationDateCard from "components/company/v2/cards/ExchangeRegistrationDateCard";
import FoundedYearCard from "components/company/v2/cards/FoundedYearCard";
import HeadquartersCard from "components/company/v2/cards/HeadquartersCard";
import IndexParticipationCard from "components/company/v2/cards/IndexParticipationCard";
import MarketCapCard from "components/company/v2/cards/MarketCapCard";
import NextUpcomingCalendarEventCard from "components/company/v2/cards/NextUpcomingCalendarEventCard";
import RegistrationDateOrIPOCard from "components/company/v2/cards/RegistrationDateOrIPOCard";
import Financials from "components/financials/modern/Financials";
import LocalFinancialsState from "components/financials/modern/LocalFinancialsState";
import Rating from "components/rating/Rating";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import useChangeSetting from "components/settings/hooks/useChangeSetting";
import TwoPassInViewRender from "components/shared/TwoPassInViewRender";
import OperatingSegments from "components/stocks/modern/OperatingSegments/OperatingSegments";
import OperatingSegmentsDataProvider from "components/stocks/modern/OperatingSegments/OperatingSegmentsDataProvider";
import { ScrollJumpButton, SectionBeacon, SpanPixelBeacon, StickyNavbarJumpers } from "components/ui/ScrollJump";
import classNames from "components/ui/classNames";

import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import getBaseUrl from "lib/utils/getBaseUrl";
import { palette } from "lib/miolo";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";

import { useAmplitude } from "../../lib/amplitude/Amplitude";
import useSelfie from "../../lib/hooks/useSelfie";
import CloseRemessaOnlineDiscount from "../../lib/queries/CloseRemessaOnlineDiscount";
import type {
  ICloseRemessaOnlineDiscountMutation,
  ICloseRemessaOnlineDiscountMutationVariables,
} from "../../lib/queries/__generated__/CloseRemessaOnlineDiscount.generated";
import { IAssetType, IIndicatorDataSource, ISecurityCountry } from "../../types/graphqlTypes";
import RemessaOnlineDiscountAdvertisement from "../advertisements/RemessaOnlineDiscountAdvertisement";
import AssetDiscussion from "../discussions/AssetDiscussion";

import { ContentSection } from "./sections/ContentSection";
import ContentSectionBreadcrumbs from "./sections/ContentSectionBreadcrumbs";
import AmericanIssuerDividendsCollapsible from "./sections/collapsible/AmericanIssuerDividendsCollapsible";
import AmericanIssuerExecutiveCompensationCollapsible from "./sections/collapsible/AmericanIssuerExecutiveCompensationCollapsible";
import CorporateActionsCollapsible from "./sections/collapsible/CorporateActionsCollapsible";

interface AmericanCompanyProps {
  issuer: IAmericanCompanyIssuerTopLevelFragment;
  tickerSymbol: string;
  security: IAmericanCompanyIssuerTopLevelFragment["securities"][0];
}

export default function AmericanCompany({ issuer, tickerSymbol, security }: AmericanCompanyProps) {
  const user = useSelfie();
  const amplitude = useAmplitude();

  useEffect(() => {
    amplitude.logEvent("Viewed issuer", {
      category: "Stocks",
      issuerId: issuer._id,
      tickerSymbol,
      name: issuer.name,
      sector: issuer.sector,
    });
  }, [issuer._id]);

  const { primaryTickerSymbol } = issuer;
  const pageTitle = `${primaryTickerSymbol} - ${issuer.name} | Fundamentei`;
  const description = `${primaryTickerSymbol} - ${issuer.name}: Análise Fundamentalista, Cotação, Histórico de Balanços, Dividendos (DY), Indicadores (P/L, P/VPA) e muito mais!`;
  const gitShaShort = process.env.GIT_SHA_SHORT || "1";
  const imageUrl = `${getBaseUrl()}/og/issuer/${primaryTickerSymbol.toLowerCase()}?ns=us&v=${gitShaShort}`;

  const [activeContentSections, setActiveContentSections] = useState<ContentSection[]>([]);

  const isContentSectionActive = useCallback(
    (contentSection: ContentSection) => activeContentSections.includes(contentSection),
    [activeContentSections],
  );

  const toggleActiveContentSection = useCallback(
    (nextContentSection: ContentSection) => {
      return () => {
        if (activeContentSections.includes(nextContentSection)) {
          setActiveContentSections(
            activeContentSections.filter((candidate) => {
              return candidate !== nextContentSection;
            }),
          );
        } else {
          setActiveContentSections(activeContentSections.concat(nextContentSection));
        }
      };
    },
    [activeContentSections],
  );

  const { value: currentTableSettings } = useChangeSetting("financialsTable", {
    sortDirection: "ASC",
    textAlignment: "CENTER",
    fullBleed: false,
  });

  return (
    <div>
      <FundamenteiMetadata
        title={pageTitle}
        description={description}
        openGraph={{
          images: [
            {
              url: imageUrl,
              width: 1200,
              height: 630,
              alt: `${primaryTickerSymbol} - ${issuer.name} no Fundamentei`,
            },
          ],
        }}
      />

      <SpanPixelBeacon offset={-200} id={SectionJumpIdentifiers.Cover} />
      <div className="mx-auto max-w-7xl space-y-8 px-4 pb-8 xl:px-0">
        <div
          className={classNames(
            "relative mt-12 space-y-8 rounded-xl bg-white px-4 pb-8 pt-8 shadow",
            "border border-slate-200 ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100",
          )}
        >
          <div className="px-4">
            <CompanyIssuerHeaderCard issuer={issuer} security={security} tickerSymbol={tickerSymbol} />
          </div>

          <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
            <div className="space-y-7">
              <div className="w-full space-y-2 px-4">
                <h6 className="font-aeonik text-sm font-bold uppercase tracking-wide text-gray-600">Ratings</h6>
                <Rating
                  issuer={issuer}
                  rankingPlacement={issuer.overallRankingPlacement}
                  rankingRatingSummary={issuer.overallRankingRatingSummary}
                />
              </div>

              <CompanyIssuerGovernance issuer={issuer} />
            </div>

            <CompanyIssuerRatings issuer={issuer} />
          </div>
        </div>

        {user && !user.settings.advertisements.hasClosedRemessaOnlineDiscount && (
          <div className="w-full">
            <Mutation<ICloseRemessaOnlineDiscountMutation, ICloseRemessaOnlineDiscountMutationVariables>
              mutation={CloseRemessaOnlineDiscount}
            >
              {(closeRemessaOnlineDiscount) => (
                <RemessaOnlineDiscountAdvertisement onClose={closeRemessaOnlineDiscount} />
              )}
            </Mutation>
          </div>
        )}

        <StickyNavbarJumpers>
          <ScrollJumpButton id={SectionJumpIdentifiers.Cover}>
            <FormattedMessage defaultMessage="Início" id="m42V4+" />
          </ScrollJumpButton>

          <ScrollJumpButton id={SectionJumpIdentifiers.Liquidity}>
            <FormattedMessage defaultMessage="Cotação" id="zV6SED" />
          </ScrollJumpButton>

          <ScrollJumpButton id={SectionJumpIdentifiers.Revenues}>
            <FormattedMessage defaultMessage="Receitas" id="DB2cb9" />
          </ScrollJumpButton>

          <ScrollJumpButton id={SectionJumpIdentifiers.Financials}>
            <FormattedMessage defaultMessage="Balanços" id="wauBuH" />
          </ScrollJumpButton>

          <ScrollJumpButton id={SectionJumpIdentifiers.Discussions}>
            <FormattedMessage defaultMessage="Discussões" id="aDdimF" />
          </ScrollJumpButton>
        </StickyNavbarJumpers>

        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <MarketCapCard issuer={issuer} />
          <FoundedYearCard issuer={issuer} />
          <RegistrationDateOrIPOCard issuer={issuer} />
          <ExchangeRegistrationDateCard issuer={issuer} />
          <IndexParticipationCard issuer={issuer} />
          <HeadquartersCard issuer={issuer} />
          <EmployeesCountCard issuer={issuer} />
          {issuer.nextUpcomingCalendarEvent && <NextUpcomingCalendarEventCard issuer={issuer} />}
        </div>

        <div className="w-full">
          <StaticContentSection primaryColor={palette.oceanGreen} className="items-center gap-y-3">
            <div className="col-span-7 md:col-span-4">
              <ContentSectionHeading>Valuation</ContentSectionHeading>
              <ContentSectionBreadcrumbs segments={["Company Financials", "Valuation Indicators"]} />
            </div>

            <div className="col-span-1" />

            <div className="col-span-12 md:col-span-7">
              <div className="flex space-x-4">
                {issuer.securities.map((security) => {
                  const linkHref = `/us/${security.tickerSymbol.toLowerCase()}/valuation`;
                  const linkAs = `/[country]/[tickerSymbol]/valuation`;

                  return (
                    <Link
                      key={security.tickerSymbol}
                      as={linkHref}
                      href={linkAs}
                      className={classNames(
                        "flex rounded-full bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-800",
                        "border border-transparent",
                        "hover:border-emerald-600",
                      )}
                    >
                      {security.tickerSymbol}
                    </Link>
                  );
                })}
              </div>
            </div>
          </StaticContentSection>
        </div>

        <ExecutivesAndDirectors
          tickerSymbol={tickerSymbol}
          isExpanded={isContentSectionActive(ContentSection.EXECUTIVE_AND_DIRECTORS)}
          onToggleCollapsed={toggleActiveContentSection(ContentSection.EXECUTIVE_AND_DIRECTORS)}
        />

        {user && user.isPremium && (
          <AmericanIssuerExecutiveCompensationCollapsible
            tickerSymbol={tickerSymbol}
            isREIT={issuer.isREIT}
            isExpanded={isContentSectionActive(ContentSection.EXECUTIVE_COMPENSATION)}
            onToggleCollapsed={toggleActiveContentSection(ContentSection.EXECUTIVE_COMPENSATION)}
          />
        )}

        <TwoPassInViewRender tag="SayOnPayChart">
          <SayOnPayChart />
        </TwoPassInViewRender>

        <TwoPassInViewRender tag="PayRatioHistoryChart">
          <PayRatioHistoryChart />
        </TwoPassInViewRender>

        <TwoPassInViewRender tag="EmployeesChart">
          <EmployeesChart tickerSymbols={[tickerSymbol]} />
        </TwoPassInViewRender>

        {user && user.isPremium && (
          <CorporateActionsCollapsible
            tickerSymbols={[tickerSymbol]}
            isExpanded={isContentSectionActive(ContentSection.CORPORATE_ACTIONS)}
            onToggleCollapsed={toggleActiveContentSection(ContentSection.CORPORATE_ACTIONS)}
          />
        )}

        {user && user.isPremium && (
          <AmericanIssuerDividendsCollapsible
            tickerSymbol={tickerSymbol}
            isExpanded={isContentSectionActive(ContentSection.DIVIDENDS)}
            onToggleCollapsed={toggleActiveContentSection(ContentSection.DIVIDENDS)}
          />
        )}

        <TrailingReturnsChart
          trailingReturns={issuer.securities.map((security) => security.trailingReturns).filter(notUndefinedOrNull)}
        />

        <TwoPassInViewRender tag="QuotesChart">
          <Provider>
            <QuotesChart tickerSymbols={[tickerSymbol]} />
          </Provider>
        </TwoPassInViewRender>

        <TwoPassInViewRender tag="IndicatorVsQuoteChart">
          <Provider>
            <IndicatorVsQuoteChart country={ISecurityCountry.Us} tickerSymbols={[tickerSymbol]} />
          </Provider>
        </TwoPassInViewRender>

        <TwoPassInViewRender tag="MarketCapitalizationChart">
          <Provider>
            <MarketCapitalizationChart tickerSymbols={[tickerSymbol]} />
          </Provider>
        </TwoPassInViewRender>

        <TwoPassInViewRender tag="TradesChart">
          <TradesChart tickerSymbols={[tickerSymbol]} />
        </TwoPassInViewRender>

        <TwoPassInViewRender tag="FreeFloatChart">
          <FreeFloatChart tickerSymbols={[tickerSymbol]} />
        </TwoPassInViewRender>

        <TwoPassInViewRender tag="SharesChart">
          <SharesChart tickerSymbols={[tickerSymbol]} />
        </TwoPassInViewRender>

        <TwoPassInViewRender tag="OperatingSegments">
          <OperatingSegmentsDataProvider tickerSymbols={[tickerSymbol]}>
            {({ businessSegments, geographicSegments, loading }) => {
              if (businessSegments && geographicSegments) {
                return (
                  <div className="relative space-y-8">
                    {!loading && <SpanPixelBeacon offset={-160} id={SectionJumpIdentifiers.Revenues} />}

                    <Provider>
                      <ErrorBoundary fallbackRender={() => null}>
                        <OperatingSegments
                          kind="BUSINESS"
                          tickerSymbols={[tickerSymbol]}
                          financialSegments={businessSegments}
                        />
                      </ErrorBoundary>
                    </Provider>
                    <Provider>
                      <ErrorBoundary fallbackRender={() => null}>
                        <OperatingSegments
                          kind="GEOGRAPHIC"
                          tickerSymbols={[tickerSymbol]}
                          financialSegments={geographicSegments}
                        />
                      </ErrorBoundary>
                    </Provider>
                  </div>
                );
              }
              return null;
            }}
          </OperatingSegmentsDataProvider>
        </TwoPassInViewRender>
        <div
          className={classNames("mx-auto max-w-7xl space-y-6 pb-16 lg:px-8 xl:px-0", {
            "max-w-full px-8 xl:px-8": currentTableSettings?.fullBleed,
          })}
        >
          <TwoPassInViewRender
            tag="Financials"
            loadingSkeletonClassName="mx-auto max-w-7xl space-y-8 pb-16 xl:px-0"
          >
            <LocalFinancialsState indicatorDataSource={IIndicatorDataSource.Morningstar}>
              {(props) => (
                <Provider>
                  <Financials {...props} country={ISecurityCountry.Us} tickerSymbol={tickerSymbol} limit={undefined} />
                </Provider>
              )}
            </LocalFinancialsState>
          </TwoPassInViewRender>
        </div>

        <div className="mx-auto max-w-7xl space-y-8 px-4 pb-16 xl:px-0">
          {issuer.youtubeVideos.length > 0 && (
            <TwoPassInViewRender tag="YouTubeVideos">
              <div className="mt-6">
                <YouTubeVideos
                  key={issuer._id}
                  youtubeVideos={user && user.isPremium ? issuer.youtubeVideos : issuer.youtubeVideos.slice(0, 1)}
                />
              </div>
            </TwoPassInViewRender>
          )}

          <TwoPassInViewRender tag="AssetDiscussion">
            <SectionBeacon id={SectionJumpIdentifiers.Discussions}>
              <AssetDiscussion
                assetType={issuer.isREIT ? IAssetType.AmericanReit : IAssetType.AmericanCompany}
                assetId={issuer._id}
              />
            </SectionBeacon>
          </TwoPassInViewRender>
        </div>
      </div>
    </div>
  );
}
